import React from 'react';
import { useContext } from 'react';
import { Navigate, useOutlet } from 'react-router-dom';
import { observer } from 'mobx-react';
import { AppContext } from './AppContext';

import { PanelCalculatingView } from './views/PanelCalculatingView';
import { route } from './routes';
import { useApi} from '/@/app/Api';
import { tipState, injectApi } from '/@/app/components/TipOfTheDay/TipState.js';


//
// Important Part of User Flow.
//
// Guard asking for actions before the app becomes available.
// Redirects to special views, until user completes all the actions required.
//
// See `states/userFlowState.js` for the details.
//
export const ViewRedirectionGuard = observer(({ userFlowState }) => {
    const api=useApi();
    injectApi(api);

    const outlet = useOutlet();
    const { featureEnabled } = useContext(AppContext);

    if (!userFlowState.initialized) return null;

    const allGuardPaths = {
        acceptedTerms: route('/accept-terms/'),
        registrationComplete: route('/finish-registration/'),
        introVideo: route('/intro/'),
    }

    //
    // User should accept terms.
    //
    if (!userFlowState.acceptedTerms) {
        if (allGuardPaths.acceptedTerms === location.pathname) {
            return outlet;
        }
        return <Navigate to={allGuardPaths.acceptedTerms} replace />;
    }

    //
    // User should set age and gender.
    //
    if (!userFlowState.registrationComplete) {
        if (allGuardPaths.registrationComplete === location.pathname) {
            return outlet;
        }
        return <Navigate to={allGuardPaths.registrationComplete} replace />;
    }

    //
    // Show an introductory video.
    //
    if (!userFlowState.video_2024_08_watched) {
        if (allGuardPaths.introVideo === location.pathname) {
            return outlet;
        }
        return <Navigate to={allGuardPaths.introVideo} replace />;
    }

    //
    // Show a view / backdrop with calculation spinner.
    //
    if (
        !location.pathname.startsWith(userFlowState.calculatePostponedForLocation)
        && (
            userFlowState.partialCalculateRequired
            || featureEnabled('general_analytics') && userFlowState.calculateRequired
        )
    ) {
        userFlowState.startCalculation();  // idempotent call

        // FIXME do not replace the view with the backdrop - add it along the view instead.
        return <PanelCalculatingView userFlowState={userFlowState} />;
    } else {
        //
        // Tip of the day.
        // Shouldn't fetch tip if calculation started from the page load,
        // to prevent double work on backend (and possible deadlock).
        tipState.init();
    }

    return outlet;
});
